import {
  TmPhaseId,
  TimeMaterialPhaseRequest,
  TimeMaterialPhaseResponse,
  EstimateGroupId,
  TMFeeId,
  VeryCompactWorkOrderResponse,
  ClientOrderResponse,
  ClientUpgradeResponse,
  ClientUpgradePreviewResponse,
  VVCompactTimeMaterialResponse,
  BillTimeMaterialResponse,
  TimeMaterialSearchResponse,
} from './../../entities/tm/TimeMaterialResponse';
import { ClientListItemPreviewResponse, ClientListItemResponse, EstimatorTemplateListWithSelectionResponse } from '@business/entities/tm/EstimatorTemplateListResponse';
import { TMAdjustmentResponse, TMCategoryAdjustmentRequest, TMFeeAdjustmentRequest } from '@business/entities/tm/TMAdjustmentResponse';
import {
  OrderActivityPageQuery,
  OrderEstimateId,
  OrderId,
  OrderProductId,
  OrderRequest,
  OrderStageId,
  OrderVendorId,
} from '@business/entities/order';
import { OrgId, JustAddressReponse, PaymentId } from '@business/entities/organization';
import { InvoiceId, TransactionInvoiceResponse } from '@business/entities/TransactionInvoice';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';
import { Endpoint } from '../endpoint';
import { Paginate } from './../../entities/common/Paginate';
import { OrderActivityResponse } from './../../entities/order/OrderActivityResponse';
import {
  EstimateLineItem,
  OrderCancelRequest,
  OrderEmailTemplateRequest,
  OrderShippingRequest,
  OrderWithItemsResposne,
  ReceiveItemRequest,
  LaborReportRecords,
} from './../../entities/order/OrderResponse';
import { OrderShipmentRequest, OrderShipmentStatus } from './../../entities/order/OrderShipmentResponse';
import { PurchaseOrderId } from './../../entities/order/PurchaseOrderResponse';
import {
  CompactTimeMaterialResponse,
  TimeMaterialResponse,
  TimeMaterialStatus,
  OrderEstimateResponse,
  TimeMaterialId,
  ProductStatus,
  TimeMaterialTemplateId,
} from '@business/entities/tm';
import { sendEmailTimeTrackingRequest, TimeLogResponse } from '@business/entities/time_log';
import { UserId, CompactUserResponse } from '@business/entities/account';
import { FileResponse } from '@business/entities/file';
import { POStatusID } from '@business/entities/poStatus';
import { ProcessTmInvoiceFormData } from '@modules/organizations/OrganizationPage/Components/Billing/TimeMaterialPaymentModal/TimeMaterialPaymentModal';
import { QBSalesTaxResponse } from '@business/entities/salesTax';
import { GeneratePoProductFormData } from '@modules/timeMaterials/TimeMaterialOverlay/Tabs/GeneratePoModal/GeneratePoModal';
import { CommentResponse } from '@business/entities/comment';
import { TaskPriority } from '@business/constants/task';
import {
  CompactTimeMaterialSectionResponse,
  TimeMaterialSectionResponse,
  TimeMaterialStepResponse,
} from '@business/entities/tm/TimeMaterialSectionResponse';
import { TMTransactionInvoiceFormData } from '@modules/timeMaterials/TimeMaterialOverlay/Tabs/EditTransactionInvoiceOverlay/EditTransactionInvoiceModal';
import { TMOrganizationDashboardResponse } from '@business/entities/tm/TMOrganizationDashboardResponse';
import { TMClientDashboardResponse, PatchClientDashboardRequest } from '@business/entities/tm/TMClientDashboardResponse';

export function fetchOrders(orgId?: OrgId) {
  return Endpoint.get<CompactTimeMaterialResponse[]>(`@gc/time-materials`, { params: { orgId } });
}

export function fetchCompactOrders() {
  return Endpoint.get<VeryCompactWorkOrderResponse[]>(`@gc/time-materials`, {});
}

export function fetchOrgOrders(orgId?: OrgId) {
  return Endpoint.get<TimeMaterialResponse[]>(`@gc/time-materials/org`, { params: { orgId } });
}

export function fetchByOrgs(orgIds: OrgId[]) {
  const params = R.objectCompact({ orgIds });
  return Endpoint.get<BillTimeMaterialResponse[]>(`@gc/time-materials/by-orgs`, { params });
}

export function fetchVeryCompactOrders(orgId?: OrgId, status?: TimeMaterialStatus) {
  const params = R.objectCompact({ orgId, status });
  return Endpoint.get<VeryCompactWorkOrderResponse[]>(`@gc/time-materials/compact-orders`, { params });
}

export function searchOrders(search: string) {
  return Endpoint.get<TimeMaterialSearchResponse[]>(`@gc/time-materials/search`, { params: { search } });
}

export function selectManager(orderId: OrderId, manager: UserId) {
  return Endpoint.patch<CompactUserResponse>(`@gc/time-materials/select-manager/${orderId}`, { data: { manager } });
}

export function updateUsers(orderId: TimeMaterialId, users: string[]) {
  return Endpoint.post<string[]>(`@gc/time-materials/update-users/${orderId}`, { data: { users } });
}

export function fetchOrder(id: OrderId, isInvoice?: boolean, isPublic = false, withLogo = false) {
  if (isPublic) {
    return Endpoint.get<TimeMaterialResponse>(`@gc/public/time-materials/order/${id}`, { params: { isInvoice, withLogo } });
  }
  return Endpoint.get<TimeMaterialResponse>(`@gc/time-materials/order/${id}`, { params: { isInvoice, withLogo } });
}

export function createOrder(data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials`, { formData, hasFiles: true });
}

export function updateOrder(id: OrderId, data: Partial<OrderRequest>, isSend = false) {
  return Endpoint.patch<TimeMaterialResponse>(`@gc/time-materials/${id}`, { data: { ...data, isSend } });
}

export function updateEstimate(id: OrderId, data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  formData.append('_method', 'patch');
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/estimate/${id}`, { formData, hasFiles: true });
}

export function updateInvoice(id: InvoiceId, data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  formData.append('_method', 'patch');
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-material-invoice/${id}`, { formData, hasFiles: true });
}

export function updateInvoicePercentage(tmId: TimeMaterialId, data: TMTransactionInvoiceFormData) {
  return Endpoint.patch<TransactionInvoiceResponse>(`@gc/time-material-invoice/invoice-percentage/${tmId}`, { data });
}

export function updateInvoiceDueDate(id: InvoiceId, dueDate: number) {
  return Endpoint.patch<TransactionInvoiceResponse>(`@gc/time-material-invoice/due-date/${id}`, { data: { dueDate } });
}

export function createInvoice(id: OrderId, data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-material-invoice/${id}`, { formData, hasFiles: true });
}

export function deleteOrder(id: OrderProductId) {
  return Endpoint.delete<void>(`@gc/time-materials/${id}`);
}
export function acceptEstimate(id: OrderEstimateId, name: string, email: string) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/accepted/${id}`, { data: { name, email } });
}
export function createOrderInvoice(id: OrderEstimateId, invoiceId: InvoiceId, sent: boolean, data?: Partial<OrderRequest>) {
  const formData = CommonService.toFormData(data);
  formData.append('sent', sent ? 'true' : 'false');
  formData.append('invoiceId', invoiceId);
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-material-invoice/send/${id}`, { formData, hasFiles: true });
}

export function sendEstimate(id: OrderEstimateId, data?: Partial<OrderRequest>) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/estimate-send/${id}`, { data });
}

export function updateStage(
  id: OrderId,
  stage: OrderStageId,
  deliveryDate?: number,
  closeIt = false,
  sendIt?: boolean,
  emailData?: OrderEmailTemplateRequest,
) {
  const formData = CommonService.toFormData({ ...emailData, stage, deliveryDate, closeIt, sendIt });
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/stage/${id}`, { formData, hasFiles: true });
}

export function updateStatus(id: OrderId, status: TimeMaterialStatus, reason?: string, isSend?: boolean, data?: OrderCancelRequest) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/status/${id}`, { data: { ...data, status, isSend } });
}

export function updateShipping(id: OrderId, stage: OrderStageId, data: OrderShippingRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/shipping/${id}/${stage}`, { formData, hasFiles: true });
}

export function rejectEstimate(id: OrderEstimateId, reason: string) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/rejected/${id}`, { data: { reason } });
}
export function sendPurchaseOrder(id: OrderId, vendorId: OrderVendorId, data: Partial<OrderEmailTemplateRequest>, poId: string) {
  const formData = CommonService.toFormData(data);
  formData.append('vendorId', vendorId || '0');
  formData.append('purchaseOrderId', poId || '0');
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/vendor-order/${id}`, { formData, hasFiles: true });
}

export function createPurchaseOrder(id: OrderId, vendorId?: OrderVendorId, address?: JustAddressReponse) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/po-create/${id}`, { data: { vendorId, address } });
}

export function closePurchaseOrder(id: PurchaseOrderId) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/purchase-close/${id}`, { data: {} });
}
export function download(orderId: OrderId, vendorId: OrderVendorId, filename: string) {
  return Endpoint.get<void>(`@gc/time-materials/po-download/${orderId}/${vendorId}`, { isFile: true, filename });
}

export function downloadLading(id: PurchaseOrderId, filename: string) {
  return Endpoint.get<void>(`@gc/purchase-orders/lading/${id}`, { isFile: true, filename });
}

export function downloadPickList(id: PurchaseOrderId, filename: string) {
  return Endpoint.get<void>(`@gc/purchase-orders/pick-list/${id}`, { isFile: true, filename });
}

export function purchaseOrderStatus(poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/orders/po-status/${poId}`, { data: { ...data, status } });
}

export function updatePoStatus(poId: PurchaseOrderId, status: POStatusID) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/orders/update-po-status/${poId}`, { data: { status } });
}

export function updatePurchaseOrder(id: PurchaseOrderId, data: Partial<OrderRequest>) {
  const formData = CommonService.toFormData(data);
  formData.append('_method', 'patch');
  return Endpoint.post<TimeMaterialResponse>(`@gc/purchase-orders/${id}`, { formData, hasFiles: true });
}

export function saveBillOfLading(id: PurchaseOrderId, data: Partial<OrderRequest>) {
  return Endpoint.patch<TimeMaterialResponse>(`@gc/purchase-orders/pick-list/${id}`, { data });
}

export function savePickList(id: PurchaseOrderId, data: Partial<OrderRequest>) {
  return Endpoint.patch<TimeMaterialResponse>(`@gc/purchase-orders/pick-list/${id}`, { data });
}

export function createShipment(poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>, isSend?: boolean) {
  const formData = CommonService.toFormData({ ...data, status, isSend });
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/shipment/${poId}`, { formData, hasFiles: true });
}

export function createShipmentWithOutPO(id: TimeMaterialId, data: Partial<OrderShipmentRequest>, isSend?: boolean) {
  const formData = CommonService.toFormData({ ...data, isSend });
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/shipment-without-po/${id}`, { formData, hasFiles: true });
}

export function fetchActivities(options: OrderActivityPageQuery) {
  const params = R.objectCompact(options);
  return Endpoint.get<OrderActivityResponse[]>(`@gc/time-material-activity`, { params });
}

export function fetchPaginateActivities(options: OrderActivityPageQuery) {
  const params = R.objectCompact(options);
  return Endpoint.get<Paginate<OrderActivityResponse>>(`@gc/time-material-activity`, { params });
}

export function updateShipment(id: OrderId, data: OrderShippingRequest, isSend?: boolean) {
  const formData = CommonService.toFormData({ ...data, isSend });
  formData.append('_method', 'patch');
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/shipment/${id}`, { formData, hasFiles: true });
}

export function deleteShipment(id: OrderId) {
  return Endpoint.delete<TimeMaterialResponse>(`@gc/time-materials/shipment/${id}`);
}

export function deleteInvoice(invoiceId: InvoiceId, orderId: OrderId) {
  return Endpoint.delete<TimeMaterialResponse>(`@gc/orders/invoice/${invoiceId}`, { data: { orderId } });
}

export function openedEstimate(id: OrderEstimateId) {
  return Endpoint.post<void>(`@gc/orders/estimate-opened/${id}`);
}

export function searchOrderItems(search: string) {
  return Endpoint.get<OrderWithItemsResposne[]>(`@gc/time-materials/search-order`, { params: { search } });
}

export function updateEstimateLineItem(id: number, data: Partial<EstimateLineItem>) {
  return Endpoint.patch<OrderEstimateResponse>(`@gc/time-materials/estimate-item/${id}`, { data });
}

export function updateItemsStatus(orderId: string, status: ProductStatus, itemIds: number[], date?: number) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/estimate-item-status/${orderId}`, { data: { status, itemIds, date } });
}

// Time Material Phases
export function fetchPhases(timeMaterialId: TimeMaterialId) {
  return Endpoint.get<TimeMaterialPhaseResponse[]>(`@gc/time-material/phase/${timeMaterialId}`);
}
export function fetchPhase(phaseId: TmPhaseId) {
  return Endpoint.get<TimeMaterialPhaseResponse>(`@gc/time-material/phase/${phaseId}`);
}
export function createPhase(timeMaterialId: TimeMaterialId, data: Partial<TimeMaterialPhaseRequest>) {
  return Endpoint.post<TimeMaterialPhaseResponse>(`@gc/time-material/phase/${timeMaterialId}`, { data });
}
export function updatePhase(phaseId: TmPhaseId, data: Partial<TimeMaterialPhaseRequest>) {
  return Endpoint.patch<TimeMaterialPhaseResponse>(`@gc/time-material/phase/${phaseId}`, { data });
}
export function updatePhaseDate(phaseId: TmPhaseId, date: number) {
  return Endpoint.post<TimeMaterialPhaseResponse>(`@gc/time-material/phase/update-date/${phaseId}`, { data: { date: date } });
}
export function deletePhase(phaseId: TmPhaseId) {
  return Endpoint.delete<void>(`@gc/time-material/phase/${phaseId}`);
}
export function sortPhases(ids: number[]) {
  return Endpoint.post<void>(`@gc/time-material/phase/sort`, { data: { ids } });
}
export function orderItems(timeMaterialId: TimeMaterialId, items: any[]) {
  return Endpoint.post<TimeMaterialPhaseResponse[]>(`@gc/time-material/phase/sort-items/${timeMaterialId}`, { data: { items } });
}
export function receiveItems(id: OrderId, data: ReceiveItemRequest[]) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/receive-items/${id}`, { data: { data } });
}
export function getReceiveItems(id: OrderId) {
  return Endpoint.get<ReceiveItemRequest[]>(`@gc/time-materials/receive-items/${id}`);
}
export function getTimeTrackingReport(id: OrderId) {
  return Endpoint.get<LaborReportRecords[]>(`@gc/time-materials/time-tracking-report/${id}`);
}

export function downloadTimeTrackingReport(id: OrderId) {
  return Endpoint.get<void>(`@gc/time-materials/download-time-tracking-report/${id}`, { isFile: true });
}

export function downloadProgressReportReport(id: OrderId) {
  return Endpoint.get<void>(`@gc/time-materials/download-project-progress-report/${id}`, { isFile: true });
}

export function sendEmail(orderId: string, data: sendEmailTimeTrackingRequest) {
  return Endpoint.post<TimeLogResponse>(`@gc/time-materials/send-email/${orderId}`, { data });
}

export function getFiles(id: OrderId, isPublic = false) {
  return Endpoint.get<FileResponse[]>(`${isPublic ? `@gc/public/time-materials/files/${id}` : `@gc/time-materials/files/${id}`}`);
}

export function updateDates(orderId: OrderId, data: Partial<CompactTimeMaterialResponse>) {
  return Endpoint.patch<CompactTimeMaterialResponse>(`@gc/time-materials/dates/${orderId}`, { data });
}

export function processTmInvoicePayment(id: TimeMaterialId, paymentId: PaymentId, amount: number, data: ProcessTmInvoiceFormData) {
  const allData = { ...data, amount, paymentId } as any;
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/process-invoice-payment/${id}`, { data: allData });
}

export function getSalesTaxOptions() {
  return Endpoint.get<QBSalesTaxResponse[]>(`@gc/time-materials/sales-tax-options`);
}

export function createBulkPurchaseOrder(id: OrderId, address: JustAddressReponse, data: GeneratePoProductFormData) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-materials/po-generate/${id}`, { data: { address, data } });
}

// Time Material Phases

// Time Material Order Estimate Groups

// Summary tab adjustments: saving needs the note; each change is kept in the line's history (newest first)
export function updateAdjustment(id: EstimateGroupId, data: TMCategoryAdjustmentRequest) {
  return Endpoint.patch<TimeMaterialResponse>(`@gc/time-materials/adjustment/${id}`, { data });
}

export function fetchAdjustments(id: EstimateGroupId) {
  return Endpoint.get<TMAdjustmentResponse[]>(`@gc/time-materials/adjustment/${id}`);
}

export function updateFeeAdjustment(id: TMFeeId, data: TMFeeAdjustmentRequest) {
  return Endpoint.patch<TimeMaterialResponse>(`@gc/time-materials/fee-adjustment/${id}`, { data });
}

export function fetchFeeAdjustments(id: TMFeeId) {
  return Endpoint.get<TMAdjustmentResponse[]>(`@gc/time-materials/fee-adjustment/${id}`);
}

export function orderTodos(timeMaterialId: TimeMaterialId, items: any) {
  return Endpoint.post<TimeMaterialResponse>(`@gc/time-material/sections/order-todos/${timeMaterialId}`, { data: { items } });
}

/** Section **/
export function getTimeMaterialSections(id: TimeMaterialId) {
  return Endpoint.get<CompactTimeMaterialSectionResponse[]>(`@gc/time-material/sections/${id}`);
}
export function createSection(id: TimeMaterialId, name: string, tenantId?: string, isPublic = false) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/section/${id}`, { data: { name, tenantId, isPublic } });
}

export function updateSection(id: string, name: string, tenantId?: string, isPublic = false, timeMaterialId?: TimeMaterialId) {
  return Endpoint.patch<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/section/${id}`, {
    data: { name, tenantId, isPublic, timeMaterialId },
  });
}

export function deleteSection(id: string, tenantId?: string) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/delete-section/${id}`, { data: { tenantId } });
}

export function duplicateSection(id: string, tenantId?: string) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/duplicate-section/${id}`, { data: { tenantId } });
}

export function sortSections(ids: string[], tenantId?: string) {
  return Endpoint.post<void>(`@gc/time-material/sections/sort-section`, { data: { ids, tenantId } });
}

/** Step **/
export function fetchStep(id: string, tenantId?: string) {
  return Endpoint.get<TimeMaterialStepResponse>(`@gc/time-material/sections/step/${id}`, { params: { tenantId } });
}

export function loadStep(stepId: number) {
  return Endpoint.get<TimeMaterialSectionResponse>(`@gc/time-material/sections/load-step/${stepId}`);
}

export function toggleStep(stepId: number, completeAll?: boolean, tenantId?: string) {
  return Endpoint.post<TimeMaterialSectionResponse>(`@gc/time-material/sections/toggle-step/${stepId}`, { data: { completeAll, tenantId } });
}

export function createStep(
  id: string,
  data: Partial<TimeMaterialStepResponse>,
  parentStepId?: string,
  tenantId?: string,
  timeMaterialId?: TimeMaterialId,
) {
  data.parentId = parentStepId;
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/step/${id}`, { data: { ...data, tenantId, timeMaterialId } });
}

export function duplicateStep(id: string, tenantId?: string) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/duplicate-step/${id}`, { data: { tenantId } });
}

export function updateStep(id: string, data: Partial<TimeMaterialStepResponse>, tenantId?: string) {
  return Endpoint.patch<TimeMaterialSectionResponse>(`@gc/time-material/sections/step/${id}`, { data: { ...data, tenantId } });
}

export function deleteStep(id: string, tenantId?: string) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/delete-step/${id}`, { data: { tenantId } });
}

export function sortSectionSteps(ids: string[], tenantId?: string) {
  return Endpoint.post<void>(`@gc/time-material/sections/sort-step`, { data: { ids, tenantId } });
}

export function assignStep(stepId: string, data: Partial<TimeMaterialStepResponse>) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/step-assign/${stepId}`, { data });
}

export function assignSection(timeMaterialId: TimeMaterialId, sectionId: string, users: UserId[]) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/section-assign/${sectionId}`, { data: { users, timeMaterialId } });
}

export function assignSectionUsers(timeMaterialId: TimeMaterialId, sectionId: string, users: UserId[]) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/section-assign-users/${sectionId}`, {
    data: { users, timeMaterialId },
  });
}

export function createStepComment(id: string, data: Partial<CommentResponse>) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/step-comment/${id}`, { data });
}

export function sortTimeMaterialSectionSteps(step: TimeMaterialStepResponse, toColumn: any, toPosition: number) {
  return Endpoint.post<TimeMaterialStepResponse[]>(`@gc/time-material/sections/sort-time-material-step`, { data: { step, toColumn, toPosition } });
}

export function sortTimeMaterialSections(
  section: TimeMaterialSectionResponse,
  fromPosition: any,
  toPosition: number,
  timeMaterialId: TimeMaterialId,
) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/sort-time-material-section`, {
    data: { section, fromPosition, toPosition, timeMaterialId },
  });
}

export function assignTimeMaterialStep(stepId: string, data: string[]) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/assign-time-material-step/${stepId}`, { data: { data } });
}

export function setTimeMaterialStepPriority(stepId: string, priority: TaskPriority) {
  return Endpoint.post<TimeMaterialSectionResponse[]>(`@gc/time-material/sections/set-time-material-step-priority/${stepId}`, { data: { priority } });
}

export function selectTemplate(timeMaterialId: TimeMaterialId, templateId: TimeMaterialTemplateId) {
  return Endpoint.patch<CompactTimeMaterialResponse>(`@gc/time-materials/select-template/${timeMaterialId}`, { data: { templateId } });
}

export function changeTemplate(timeMaterialId: TimeMaterialId, templateId: TimeMaterialTemplateId) {
  return Endpoint.patch<CompactTimeMaterialResponse>(`@gc/time-materials/change-template/${timeMaterialId}`, { data: { templateId } });
}

export function fetchCompactOrder(id: OrderId, isCompact = true) {
  return Endpoint.get<ClientOrderResponse>(`@gc/time-materials/order/${id}`, { params: { isCompact } });
}

export function fetchUpgrades(id: OrderId) {
  return Endpoint.get<ClientUpgradeResponse[]>(`@gc/time-materials/upgrades/${id}`);
}

export function fetchListItemsByWorkOrder(id: OrderId) {
  return Endpoint.get<ClientListItemResponse[]>(`@gc/time-materials/list-items/${id}`);
}

export function fetchListItem(id: string) {
  return Endpoint.get<ClientListItemPreviewResponse>(`@gc/time-materials/list-item/${id}`);
}

export function onSignedListItem(id: string, sign: string) {
  return Endpoint.patch<ClientListItemPreviewResponse>(`@gc/time-materials/signed-list-item/${id}`, { data: { sign } });
}
export function fetchUpgrade(id: OrderId) {
  return Endpoint.get<ClientUpgradePreviewResponse>(`@gc/time-materials/upgrade/${id}`);
}

export function onSignedUpgrade(upgradeId: string, sign: string) {
  return Endpoint.patch<ClientUpgradePreviewResponse>(`@gc/time-materials/signed-upgrade/${upgradeId}`, { data: { sign } });
}

export function updateProvider(id: EstimateGroupId, providerId: string) {
  return Endpoint.patch<TimeMaterialResponse>(`@gc/time-materials/provider/${id}`, { data: { providerId } });
}

export function getWorkOrderDashboard(orgId?: string) {
  return Endpoint.get<TMOrganizationDashboardResponse[]>(`@gc/time-materials/work-order-dashboard`, { params: orgId ? { orgId } : {} });
}

export function getClientDashboard(orgId?: string) {
  return Endpoint.get<TMClientDashboardResponse[]>(`@gc/time-materials/client-dashboard`, { params: orgId ? { orgId } : {} });
}

export function patchClientDashboardRow(id: string, data: PatchClientDashboardRequest) {
  return Endpoint.patch<TMClientDashboardResponse>(`@gc/time-materials/${id}/client-dashboard`, { data });
}
